import { EsignFormType } from '@buyerflo/core/schemas/esign'
import { DeleteRounded } from 'components/icons'
import Button from 'components/button'
import { Dialog, Text, View, XStack } from 'tamagui'
import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import DropZoneInner from 'components/dropzone'
import { dontCloseOnBackgroundClick } from 'components/modal'
import { useShowSnack } from 'components/snackbar'
import { PDFDocument } from 'pdf-lib'
import { usePurchaserContext } from 'purchasers/purchaserProvider'
import { useEffect, useState } from 'react'
import { useTRPC } from 'utils/trpc'

interface UploadFormModalProps {
    formType: EsignFormType
    open: boolean
    close: () => void
}

export default ({ formType, open, close }: UploadFormModalProps) => {
    const trpc = useTRPC()
    const { purchaser, setPurchaser, refreshPurchaser } = usePurchaserContext()
    const showSnack = useShowSnack()
    const getManualUploadFormUrlMutation = useMutation(
        trpc.purchaser.getManualUploadFormUrl.mutationOptions(),
    )

    const [files, setFiles] = useState<File[]>([])
    const [error, setError] = useState<string | undefined>()

    useEffect(() => {
        if (!open) {
            setFiles([])
            setError(undefined)
        }
    }, [open])

    return (
        <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) close() }}>
            <Dialog.Portal>
                <Dialog.Overlay key="overlay" backgroundColor="$overlay" />
                <Dialog.Content key="content" bordered elevate padding="$5" borderRadius="$4" gap="$4">
                    <Dialog.Close asChild>
                        <Button size="$3" circular position="absolute" top="$2" right="$2" onPress={() => close()}>
                            X
                        </Button>
                    </Dialog.Close>
                    <Dialog.Title>Upload Form</Dialog.Title>
                    <Dialog.Description>
                        <View width={450}>
                            {error ? (
                                <Text>{error}</Text>
                            ) : (
                                <>
                                    <DropZoneInner
                                        options={{
                                            accept: {
                                                'application/pdf': ['.pdf'],
                                            },
                                            maxFiles: 1,
                                        }}
                                        onUpload={files => {
                                            setFiles(files)
                                        }}
                                    />
                                    {files.length > 0 && (
                                        <>
                                            <Text>Selected file:</Text>
                                            {files.map(file => (
                                                <XStack
                                                    key={file.name}
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                >
                                                    <Text>{file.name}</Text>
                                                    <Button
                                                        size="$3"
                                                        circular
                                                        onPress={() => setFiles(files.filter(f => f !== file))}
                                                    >
                                                        <DeleteRounded />
                                                    </Button>
                                                </XStack>
                                            ))}
                                        </>
                                    )}
                                </>
                            )}
                        </View>
                    </Dialog.Description>
                    <XStack gap="$2" flexDirection="row-reverse">
                        {!error && (
                            <Button
                                disabled={files.length === 0}
                                onPress={async () => {
                                    try {
                                        const { presignedUrl } = await getManualUploadFormUrlMutation.mutateAsync(
                                            {
                                                purchaserId: purchaser.purchaserId,
                                                formType: formType.toLowerCase(),
                                            },
                                        )

                                        if (files.length > 1) {
                                            const mergedPdf = await PDFDocument.create()

                                            for (const file of files) {
                                                const arrayBuffer = await file.arrayBuffer()
                                                try {
                                                    const pdfDoc = await PDFDocument.load(arrayBuffer, {
                                                        ignoreEncryption: false,
                                                    })

                                                    const copiedPages = await mergedPdf.copyPages(
                                                        pdfDoc,
                                                        pdfDoc.getPageIndices(),
                                                    )
                                                    copiedPages.forEach(page => mergedPdf.addPage(page))
                                                } catch (error) {
                                                    console.error(error)
                                                    setError(
                                                        `Error merging ${file.name}.  This is generally caused by encrypted PDFs.`,
                                                    )
                                                    return
                                                }
                                            }

                                            const mergedPdfBytes = await mergedPdf.save()
                                            const blob = new Blob([new Uint8Array(mergedPdfBytes)], { type: 'application/pdf' })
                                            const file = new File([blob], `${formType}.pdf`)
                                            await axios.put(presignedUrl, file)
                                        } else {
                                            await axios.put(presignedUrl, files[0])
                                        }

                                        await refreshPurchaser()
                                        showSnack('Form uploaded', 'success')
                                        close()
                                    } catch (error) {
                                        showSnack('Error uploading form', 'danger')
                                        console.error(error)
                                    }
                                }}
                            >
                                Upload
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            onPress={close}
                        >
                            Cancel
                        </Button>
                    </XStack>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog>
    )
}
